/**
 * useAvailability Hook
 *
 * Fetches availability data for a listing from Firestore.
 * Queries blockedDates subcollection and confirmed bookings,
 * then expands ranges into a Set<string> of YYYY-MM-DD dates.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { formatDate } from '../../../utils/dates';

export interface BlockedDate {
  date: string;
  reason: 'booked' | 'blocked' | 'unavailable' | 'ical_sync';
}

interface UseAvailabilityReturn {
  blockedDates: BlockedDate[];
  blockedDateSet: Set<string>;
  isLoading: boolean;
  error: Error | null;
  isDateBlocked: (date: Date) => boolean;
  refetch: () => void;
}

// endDate is exclusive (checkout-style), matching the server overlap check
// in createBooking and the iCal sync docs. Legacy docs where end <= start
// still block the start night.
function expandDateRange(start: Date, end: Date): Date[] {
  if (end <= start) return [new Date(start)];
  const dates: Date[] = [];
  const current = new Date(start);
  while (current < end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function toDateString(date: Date): string {
  return formatDate(date, 'numeric');
}

export function useAvailability(listingId: string): UseAvailabilityReturn {
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    if (!listingId) {
      setBlockedDates([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    async function fetchAvailability() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dates: BlockedDate[] = [];

      // 1. blockedDates subcollection - public read in Firestore rules
      try {
        const blockedRef = collection(db, 'listings', listingId, 'blockedDates');
        const blockedSnap = await getDocs(blockedRef);

        blockedSnap.forEach((doc) => {
          const data = doc.data();
          const startDate = data.startDate instanceof Timestamp
            ? data.startDate.toDate()
            : new Date(data.startDate);
          const endDate = data.endDate instanceof Timestamp
            ? data.endDate.toDate()
            : new Date(data.endDate);
          const reason = data.reason === 'ical_sync' ? 'ical_sync' as const : 'blocked' as const;

          if (endDate > today) {
            const rangeDates = expandDateRange(
              startDate < today ? today : startDate,
              endDate
            );
            for (const d of rangeDates) {
              dates.push({ date: toDateString(d), reason });
            }
          }
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching blockedDates:', err);
        }
      }

      // 2. Pending/confirmed bookings - only readable by the booking
      // participants per Firestore rules. Skipping is safe: confirmed
      // instant-bookings are already mirrored into blockedDates by
      // createBooking, and pending bookings get rechecked server-side
      // when a guest tries to book.
      try {
        const bookingsRef = collection(db, 'bookings');
        const bookingsQuery = query(
          bookingsRef,
          where('listingId', '==', listingId),
          where('status', 'in', ['confirmed', 'pending'])
        );
        const bookingsSnap = await getDocs(bookingsQuery);

        bookingsSnap.forEach((doc) => {
          const data = doc.data();
          const checkIn = data.checkIn instanceof Timestamp
            ? data.checkIn.toDate()
            : new Date(data.checkIn);
          const checkOut = data.checkOut instanceof Timestamp
            ? data.checkOut.toDate()
            : new Date(data.checkOut);

          if (checkOut > today) {
            const rangeDates = expandDateRange(
              checkIn < today ? today : checkIn,
              checkOut
            );
            for (const d of rangeDates) {
              dates.push({ date: toDateString(d), reason: 'booked' });
            }
          }
        });
      } catch {
        // Expected for unauthenticated visitors - they don't see in-flight
        // bookings on the calendar; the server enforces no-double-book at
        // submit time.
      }

      if (!cancelled) {
        setBlockedDates(dates);
        setIsLoading(false);
      }
    }

    fetchAvailability();

    return () => {
      cancelled = true;
    };
  }, [listingId, fetchKey]);

  const blockedDateSet = useMemo(() => {
    return new Set(blockedDates.map((d) => d.date));
  }, [blockedDates]);

  const isDateBlocked = useCallback(
    (date: Date): boolean => {
      return blockedDateSet.has(toDateString(date));
    },
    [blockedDateSet]
  );

  return { blockedDates, blockedDateSet, isLoading, error, isDateBlocked, refetch };
}

export default useAvailability;
